import { FC, useContext, useEffect, useRef, useState } from 'react'
import { QuestionPage, initValue } from '../QuestionPage/QuestionPage'
import { Container } from './Box.styled'
import { QuizContext } from '../../providers/QuizProvider/QuizContext'
import { StartPage } from '../StartPage/StartPage'
import { ResultPage } from '../ResultPage/ResultPage'
import { ContactFormPage } from '../ContactFormPage/ContactFormPage'
import { ThanksPage } from '../ThanksPage/ThanksPage'
import { AnswerDataInfo } from '../Answers/AnswersBox'

import { googleActionClickNext, googleActionResult, googleActionThanks } from '../../utils/googleAnalyticsAction'
import { fbqActionClickNext, fbqActionResult, fbqActionThanks } from '../../utils/fbqAnalyticsActions'

const apiBaseUrl = process.env.REACT_APP_BASE_URL

const enum PositionType {
    StartPage = 0,
    QuestionPage = 1,
    ResultPage = 2,
    ContactFormPage = 3,
    ThanksPage = 4,
}

interface Props {
    height?: number | null
}

export const Box: FC<Props> = ({ height }) => {
    const {
        session,
        quiz_additions,
        many_result,
        after_contacts,
        currency,
        thank_link,
        thank_is_auto_redirect,
        thank_auto_redirect_link,
        form_fields,
        is_main_page_enabled,
    } = useContext(QuizContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [position, setPosition] = useState<PositionType>(0)
    const [question, setQuestion] = useState<any>(null)
    const [results, setResults] = useState<any>(null)
    const [answer, setAnswer] = useState<AnswerDataInfo>(initValue)
    const isUpdate = useRef(false)

    const hendlerResult = async () => {
        setPosition(after_contacts ? PositionType.ContactFormPage : PositionType.ResultPage)

        setQuestion(null)
        const resp = await fetch(`${apiBaseUrl}/quiz-result/${session}`)
        const results = await resp.json()
        results.sort((a: any, b: any) => b.sort - a.sort)
        if (!results?.length && !after_contacts) {
            setPosition(PositionType.ContactFormPage)
        }
        setResults(results)
    }

    const handleStart = async () => {
        await fetch(`${apiBaseUrl}/start-quiz/`, {
            method: 'POST',
            body: JSON.stringify({ session }),
            headers: { 'Content-Type': 'application/json' },
        })

        setPosition(PositionType.QuestionPage)

        const resp = await fetch(`${apiBaseUrl}/next-question/`, {
            method: 'POST',
            body: JSON.stringify({ session }),
            headers: { 'Content-Type': 'application/json' },
        })
        const result = await resp.json()
        setQuestion(result)

        if (result.thats_all) {
            await hendlerResult()
        } else {
            setQuestion(result)

            // google next action
            googleActionClickNext(1)

            fbqActionClickNext()
        }
    }
    const handleNext = async (answer: unknown) => {
        const { id: answerId } = answer as any

        if(loading) {
            return null
        }

        setLoading(true)
        await fetch(
            isUpdate.current
                ? `${apiBaseUrl}/client_answer_update/${answerId}/${session}/`
                : `${apiBaseUrl}/client-answer/`,
            {
                method: isUpdate.current ? 'PUT' : 'POST',
                body: JSON.stringify({
                    ...(answer as any),
                    session,
                    question: question.id,
                }),
                headers: { 'Content-Type': 'application/json' },
            }
        )

        isUpdate.current = false

        const resp = await fetch(`${apiBaseUrl}/next-question/`, {
            method: 'POST',
            body: JSON.stringify({ session }),
            headers: { 'Content-Type': 'application/json' },
        })
        const result = await resp.json()
        setAnswer(initValue)
        if (result.thats_all) {
            await hendlerResult()
        } else {
            setQuestion(result)

            // google next action
            googleActionClickNext(question?.sort + 2)

            fbqActionClickNext()
        }

        setLoading(false)
    }

    const handlePrev = async () => {
        if (question.answers.length === 0) {
            return
        }
        const resp = await fetch(
            `${apiBaseUrl}/client_answer_update/${question.answers[question.answers.length - 1]}/${session}/`
        )
        const { question: pervQuestion, ...result } = await resp.json()
        //setResults(null)
        setQuestion(pervQuestion)
        setAnswer(result)
        isUpdate.current = true
    }
    const timer = useRef<number | null>(null)

    const handleFormContact = (answer: unknown) => {
        fetch(`${apiBaseUrl}/leadinfo/`, {
            method: 'POST',
            body: JSON.stringify({
                ...(answer as any),
                session,
            }),
            headers: { 'Content-Type': 'application/json' },
        })

        // google thanks page action
        googleActionThanks()

        // fb
        fbqActionThanks()


        setPosition(PositionType.ThanksPage)
        if (!!after_contacts && results?.length) {
            setTimeout(() => {
                setPosition(PositionType.ResultPage)
            }, 2000)
        } else if (thank_is_auto_redirect) {
            timer.current = setTimeout(() => {
                window.open(thank_auto_redirect_link, '_blank', 'noopener')
            }, 2000) as unknown as number
        }
    }

    const handleResult = (linkTo: string) => {
        if (!after_contacts) {
            setPosition(PositionType.ContactFormPage)

            // google result action
            googleActionResult()

            //fb
            fbqActionResult()
        } else {
            if (linkTo) window.open(linkTo, '_blank', 'noreferrer')
        }
    }

    const handleThanksPage = () => {
        if (timer.current) clearTimeout(timer.current)
        window.open(thank_link, '_blank', 'noopener')
    }

    useEffect(() => {
        if (typeof is_main_page_enabled === 'boolean' && !is_main_page_enabled) {
            handleStart()
        }
    }, [is_main_page_enabled])

    return (
        <Container height={height}>
            {position === PositionType.StartPage && is_main_page_enabled && (
                <StartPage additions={quiz_additions ?? []} onStart={handleStart} />
            )}
            {position === PositionType.QuestionPage && !!question && (
                <QuestionPage
                    id={question.id}
                    title={question.title}
                    title_config={question.title_config}
                    description={question.description}
                    description_config={question.description_config}
                    type={question.type}
                    config={question.config}
                    answers={question.option_answer ?? []}
                    progressValue={question.sort}
                    progressCount={question.count_question}
                    additions={quiz_additions ?? []}
                    required={question.required}
                    answer={answer}
                    setAnswer={setAnswer}
                    onNext={handleNext}
                    onPrev={handlePrev}
                />
            )}
            {position === PositionType.ContactFormPage && (
                <ContactFormPage
                    formFields={form_fields ?? []}
                    additions={quiz_additions ?? []}
                    onNext={handleFormContact}
                />
            )}
            {position === PositionType.ResultPage && !!results && (
                <ResultPage
                    many_result={many_result}
                    results={results ?? []}
                    currency={currency}
                    additions={quiz_additions ?? []}
                    onNext={handleResult}
                    lockBonus={!after_contacts}
                />
            )}
            {position === PositionType.ThanksPage && !!results && (
                <ThanksPage additions={quiz_additions ?? []} onNext={handleThanksPage} />
            )}
            {/* <StartPage
        title="Пример заголовка в три строки. Шрифт Montserrat, 25 bold"
        title_config={{ bold: true, italic: false, underline: false }}
        description="Пример подзаголовка в несколько строк. Если строк мало, то кнопка поднимается вверх, в целом это всеё, что нужно знать об этом разделе."
        description_config={{ bold: false, italic: false, underline: false }}
        button_text = "Take quiz"
      /> */}
            {/* <QuestionPage
        title="Пример заголовка в три строки. Шрифт Montserrat, 25 bold"
        title_config={{ bold: true, italic: false, underline: false }}
        description="Пример подзаголовка в несколько строк. Если строк мало, то кнопка поднимается вверх, в целом это всеё, что нужно знать об этом разделе."
        description_config={{ bold: false, italic: false, underline: false }}
        type={7}
        config={{ is_range: false, min: 0, max: 12 }}
        answers={[{ id: 5, answer: "Hello" }]}
        progressValue={8}
        progressCount={20}
        additions={[]}
      /> */}
        </Container>
    )
}
